import { ToastService } from '../../../shared/components/toast/toast.service';
import { Router, ActivatedRoute } from '@angular/router';
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';

import { Price } from '../../../models/Price';
import { User, parseRechargeBonusFlag, receivesRechargeBonus } from '../../../models/User';
import { BonusQuote, bonusReasonLabel, isBonusUnknown } from '../../../models/WalletBonus';
import { RechargesService } from '../../../shared/services/recharges.service';
import { UserService } from '../../../shared/services/user.service';
import { WalletBonusService } from '../../../shared/services/wallet-bonus.service';
import { WalletTransferService } from '../../../shared/services/wallet-transfer.service';
import { CENT_MICROS, TOO_MANY_DECIMALS_PATTERN, WalletBalance } from '../../../models/Wallet';
import { newIdempotencyKey } from '../../../shared/utils/idempotency-key';
import { UncertainTransfer, UncertainTransferStore } from '../../../shared/services/uncertain-transfer.store';
import { validationMessage } from '../../../shared/interceptors/http-error.interceptor';
import { MoneyMicrosPipe } from '../../../shared/pipes/money-micros.pipe';
import { decimalStringToMicros, microsToDecimalString } from '../../../models/WalletPricing';
import { WalletTransfer } from '../../../shared/services/wallet-transfer.service';
import {Observable, Subscription} from 'rxjs';

/**
 * "Saldo": recarga em R$ na carteira, a mesma do "Adicionar saldo" do cliente.
 *
 * Desde 01/10/2026 (Bruno) WhatsApp e IA não têm mais recarga própria — os dois saem do
 * saldo da carteira, então a tela oferece SMS e Ligação (pacote, como antes) e Saldo. É
 * só um alvo de TELA: no corpo vai `target: 'sms'` com `credits: 0`, exatamente o que o
 * "Adicionar saldo" manda, e é o `credits: 0` (recarga por valor) que faz o backend
 * creditar o valor pago na carteira, com o bônus da faixa.
 */
export const BALANCE_TARGET = 'balance';
const BALANCE_PAYLOAD_TARGET = 'sms';

/**
 * Status 0 (a resposta não chegou) e 5xx: o backend pode ter registrado a transferência. Não é
 * recusa, então a tela não pede "tente de novo" — e, se o admin repetir, o pedido vai com a
 * mesma `idempotency_key`, que devolve a transferência já criada em vez de debitar de novo.
 */
export const TRANSFER_OUTCOME_UNKNOWN_MESSAGE =
  'Não deu para confirmar a transferência. Confira o extrato da carteira de origem antes de repetir.';

/**
 * 5xx com estes `code` é recusa certa, e não desfecho incerto: o backend responde antes de criar
 * qualquer coisa (a carteira não respondeu o saldo, ou não resolveu o preço para converter os
 * créditos). Pode tentar de novo.
 */
const NOTHING_DONE_CODES = ['wallet_unavailable', 'price_unavailable'];

/** Recusa certa (422) sem `code` conhecido nem mensagem do backend. */
const TRANSFER_REFUSED_MESSAGE = 'A transferência foi recusada. Nada saiu da carteira de origem.';

/** Transferência que já existia e falhou (replay 200 com `status: 'failed'`), sem motivo. */
const FAILED_TRANSFER_MESSAGE = 'A transferência não foi feita: a carteira recusou o débito. Nada saiu da origem.';

/** Recusa da transferência de créditos (SMS/ligação) sem mensagem do backend. */
const CREDITS_TRANSFER_REFUSED_MESSAGE = 'Erro ao transferir créditos! Tente novamente mais tarde';

/** Recarga manual sem resposta (status 0): pode ter sido lançada. */
export const RECHARGE_OUTCOME_UNKNOWN_MESSAGE =
  'Não deu para confirmar a recarga. Confira a lista de recargas antes de lançar de novo.';

/**
 * Texto da recusa por `code` (`WalletTransferException` no backend). O `code` é contrato
 * estável; o texto é o que o admin lê — o código interno não vai para a tela.
 */
const TRANSFER_REFUSAL_MESSAGES: Record<string, string> = {
  insufficient_funds: 'Saldo insuficiente na carteira de origem.',
  same_user: 'A conta de origem e a de destino precisam ser diferentes.',
  invalid_amount: 'O valor da transferência precisa ser maior que zero.',
  user_not_found: 'Conta de origem ou de destino não encontrada.',
  wallet_not_authoritative: 'A carteira ainda não é a fonte do saldo. Transferência indisponível por enquanto.',
  idempotency_key_reused:
    'Este pedido já tinha sido enviado com outros dados. Confira o extrato da carteira de origem; '
    + 'se transferir de novo, sai como uma transferência nova.',
};

const money = new MoneyMicrosPipe();

/**
 * Texto da recusa certa: o do `code` quando a tela o conhece; senão o mesmo que o interceptador
 * montaria para o 422 (o do Laravel vem com `errors` por campo) ou o `message` dos outros.
 */
function refusalMessage(res: any, fallback: string): string {
  const code = res?.error?.code;
  const backendMessage = res?.status === 422
    ? validationMessage(res?.error)
    : (typeof res?.error?.message === 'string' ? res.error.message : '');

  return (code && TRANSFER_REFUSAL_MESSAGES[code]) || backendMessage || fallback;
}

/**
 * O reenvio com a mesma chave devolve (200) a transferência que já existe — e ela pode ter
 * falhado (débito recusado: `failed` é estado final). Não é sucesso: nada se moveu. Devolve o
 * texto para a tela, ou `null` quando não é esse caso.
 */
function failedReplayMessage(transfer: WalletTransfer | null | undefined): string | null {
  if (transfer?.status !== 'failed') {
    return null;
  }

  return transfer.failure_reason || FAILED_TRANSFER_MESSAGE;
}

/**
 * Recusa certa: o pedido chegou e foi recusado antes de criar qualquer coisa. Qualquer outro
 * desfecho (0, 408 do proxy, 409, 429, 5xx, erro sem `status`) é incerto — dizer "nada saiu
 * da origem" ali seria afirmar o que a tela não sabe.
 */
const CERTAIN_REFUSAL_STATUSES = [400, 401, 403, 404, 422];

/** Desfecho incerto: não é recusa certa, nem 5xx com um `code` de "nada foi feito". */
function isUncertainOutcome(res: any): boolean {
  const status = res?.status;

  if (CERTAIN_REFUSAL_STATUSES.includes(status)) {
    return false;
  }

  return !(status >= 500 && NOTHING_DONE_CODES.includes(res?.error?.code));
}

/** Moeda do valor da transferência: `POST /admin/wallet/transfers` recebe `amount` em R$. */
export const TRANSFER_CURRENCY = 'BRL';

/** Espera entre a última tecla e a cotação, como na busca de cliente. */
const BONUS_QUOTE_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-add-credits',
  templateUrl: './add-credits.component.html',
  styleUrls: ['./add-credits.component.scss']
})
export class AddCreditsComponent implements OnInit, OnDestroy {

  users: User[] = [];
  prices: Price[] = [];

  @ViewChild('inputDestination') inputDestination: ElementRef<HTMLInputElement>;
  @ViewChild('inputOrigin') inputOrigin: ElementRef<HTMLInputElement>;

  userControlDestination = new FormControl<User | null>(null);
  filteredUserDestination$!: Observable<User[]>;

  userControlOrigin = new FormControl<User | null>(null);
  filteredUserOrigin$!: Observable<User[]>;

  /** Alvo da tela: `sms` ou `call` (pacote) ou `balance` (saldo em R$ na carteira). */
  target = 'sms';
  type = 'recharge';
  timer: any = null;

  rechargeForm: UntypedFormGroup;

  /** Lançamento em curso — o botão Adicionar/Transferir carrega. */
  isSaving = false;

  /** Transferência de Saldo: valor em R$ e o saldo da carteira da origem. */
  transferAmount = new FormControl<string | null>(null);
  originBalance: WalletBalance | null = null;
  isLoadingOriginBalance = false;
  originBalanceUnavailable = false;
  /** Texto da última transferência recusada com certeza (422, 4xx). */
  transferError: string | null = null;
  /**
   * O último pedido incerto feito NESTA visita: o aviso dele fica na tela mesmo quando o
   * formulário passa a mostrar outro par (ver `uncertainTransfer`).
   */
  private lastUncertainFingerprint: string | null = null;
  readonly transferOutcomeUnknownMessage = TRANSFER_OUTCOME_UNKNOWN_MESSAGE;
  /**
   * Com um pedido incerto na tela, mandar OUTRO pedido sai com chave nova — se o incerto foi
   * registrado, é uma segunda transferência. O admin confirma que conferiu o extrato.
   */
  confirmNewTransfer = new FormControl<boolean>(false, { nonNullable: true });

  /**
   * Chave de idempotência da tentativa lógica em curso e o payload a que ela pertence. A
   * mesma chave vale enquanto origem, destino e valor não mudarem; sucesso ou mudança no
   * pedido gera outra (ver `transferIdempotencyKeyFor`).
   */
  private transferIdempotencyKey: string | null = null;
  private transferIdempotencyFingerprint: string | null = null;
  /**
   * Consulta do saldo da origem em voo. Uma por vez: trocar a origem cancela a anterior, senão
   * a resposta atrasada da conta A pintaria o disponível de A na tela da conta B.
   */
  private originBalanceSubscription: Subscription | null = null;
  /**
   * Lançamento (transferência ou recarga) em voo. NÃO é cancelado ao sair da tela: abortar o POST
   * não desfaz o débito no servidor e apagaria o único aviso de que ele aconteceu.
   */
  private saveSubscription: Subscription | null = null;
  /**
   * A tela já foi destruída com um lançamento em voo: o desfecho chega só por toast (global), sem
   * navegar para fora da tela em que o admin está agora.
   */
  private destroyed = false;

  /**
   * Cotação do bônus daquele valor para aquele cliente (CU-86akn0zp6).
   *
   * Ela vem do BFF e não de uma conta local porque só ele enxerga as
   * configurações de acúmulo e o preço do cliente: uma faixa que existe pode
   * estar excluída para quem tem preço de assinante ou de conta, e uma conta
   * feita aqui prometeria um bônus que a recarga não daria.
   */
  bonusQuote: BonusQuote | null = null;
  isQuotingBonus = false;
  bonusQuoteError = false;

  readonly bonusReason = bonusReasonLabel;

  private quoteTimer: any = null;
  /**
   * Cotação em voo. Uma por vez: outro valor ou outro cliente cancela a anterior, senão a
   * resposta atrasada do cliente A pintaria o bônus de A na tela do cliente B.
   */
  private bonusQuoteSubscription: Subscription | null = null;

  /**
   * "Recebe bônus de recarga" da conta de destino (CU-17tm466tzkz). Pré-preenche o
   * switch; `null` = ainda não se sabe (sem conta, ou a leitura falhou).
   */
  accountReceivesBonus: boolean | null = null;
  private accountBonusSubscription: Subscription | null = null;

  get formCredits(): number {
    return +this.rechargeForm.get('credits').value;
  }

  get isRecharge(): boolean {
    return this.type === 'recharge';
  }

  get isSms(): boolean {
    return this.target === 'sms';
  }

  get isCall(): boolean {
    return this.target === 'call';
  }

  /** Saldo em R$ na carteira (o "Adicionar saldo"), em recarga ou transferência. */
  get isBalance(): boolean {
    return this.target === BALANCE_TARGET;
  }

  /**
   * Bônus é da carteira: só a recarga de Saldo tem faixa. O backend nem calcula bônus
   * para pacote de SMS/ligação (`UserRechargeService::add`, só com `credits` 0), então a
   * caixa no pacote prometia um bônus que não vinha.
   */
  get showsBonus(): boolean {
    return this.isBalance && this.isRecharge;
  }

  /**
   * Transferência de saldo entre carteiras (01/10/2026). Não passa pelo `user_recharges`: vai
   * para `POST /admin/wallet/transfers`, que debita a origem e só credita o destino depois que
   * a carteira confirma o débito (a integração é por fila).
   */
  get isBalanceTransfer(): boolean {
    return this.isBalance && !this.isRecharge;
  }

  /**
   * Valor da transferência em micros; `null` se vazio, zero, negativo, ilegível ou com mais de
   * 2 casas. Pela mesma conversão das telas de preço (`decimalStringToMicros`), sem float.
   */
  get transferAmountMicros(): number | null {
    const micros = decimalStringToMicros(this.transferAmountText);

    return micros !== null && micros > 0 && micros % CENT_MICROS === 0 ? micros : null;
  }

  /** O mesmo valor como o corpo pede: decimal de 2 casas (`"10.50"`). */
  get transferAmountDecimal(): string | null {
    const micros = this.transferAmountMicros;

    if (micros === null) {
      return null;
    }

    const [units, cents = ''] = microsToDecimalString(micros).split('.');
    return `${units}.${cents.padEnd(2, '0')}`;
  }

  get transferAmountTooManyDecimals(): boolean {
    return TOO_MANY_DECIMALS_PATTERN.test(this.transferAmountText);
  }

  /**
   * Digitou algo que não é valor: negativo, ou o expoente que o `type="number"` aceita
   * (`1e-7`). Sem o aviso o Transferir só ficava apagado, sem dizer por quê.
   */
  get transferAmountUnreadable(): boolean {
    const text = this.transferAmountText;
    return text !== '' && !this.transferAmountTooManyDecimals && decimalStringToMicros(text) === null;
  }

  private get transferAmountText(): string {
    const raw = this.transferAmount.value;
    return raw === null || raw === undefined ? '' : String(raw).trim();
  }

  /**
   * A rota recebe o valor em R$ e o compara com o disponível da origem sem converter moeda.
   * Carteira de origem em outra moeda não entra: transferir assim tiraria um valor diferente
   * do que a tela mostra.
   */
  get originCurrencyMismatch(): boolean {
    return !!this.originBalance && this.originBalance.currency !== TRANSFER_CURRENCY;
  }

  /** O valor passa do disponível da origem (quando a carteira respondeu). */
  get transferExceedsBalance(): boolean {
    const micros = this.transferAmountMicros;
    return micros !== null && !!this.originBalance && micros > this.originBalance.available_micros;
  }

  get sameTransferUser(): boolean {
    const origin = this.userControlOrigin.value;
    const destination = this.userControlDestination.value;
    return !!origin && !!destination && origin.id === destination.id;
  }

  /**
   * O pedido de transferência na tela — produto, origem, destino e quantidade —; `null` se falta
   * algum. A `description` fica de fora de propósito: no Saldo o backend compara só origem,
   * destino e valor no replay da `idempotency_key` (front-contrato-r2, 07/10), então editar as
   * Observações e repetir devolve a transferência já criada, com a observação do primeiro envio.
   */
  get transferFingerprint(): string | null {
    const origin = this.userControlOrigin.value;
    const destination = this.userControlDestination.value;
    const quantity = this.isBalance ? this.transferAmountDecimal : (this.formCredits > 0 ? String(this.formCredits) : null);

    return origin?.id && destination?.id && quantity !== null
      ? `${this.target}|${origin.id}|${destination.id}|${quantity}`
      : null;
  }

  /** `produto|origem|destino` do pedido na tela; `null` se falta origem ou destino. */
  get transferPair(): string | null {
    const origin = this.userControlOrigin.value;
    const destination = this.userControlDestination.value;

    return origin?.id && destination?.id ? `${this.target}|${origin.id}|${destination.id}` : null;
  }

  /**
   * O pedido incerto que vale para a tela, guardado FORA dela (`UncertainTransferStore`): o do
   * pedido exato na tela; senão o mais recente do mesmo par (ao voltar à tela, o aviso aparece
   * para aquele par); senão o último feito nesta visita.
   */
  get uncertainTransfer(): UncertainTransfer | null {
    return this.uncertainTransfers.find(this.transferFingerprint)
      || this.uncertainTransfers.findForPair(this.transferPair)
      || this.uncertainTransfers.find(this.lastUncertainFingerprint);
  }

  get transferOutcomeUnknown(): boolean {
    return this.uncertainTransfer !== null;
  }

  /** O pedido na tela é o incerto. */
  get isRepeatingUncertainTransfer(): boolean {
    return !!this.uncertainTransfer && this.uncertainTransfer.fingerprint === this.transferFingerprint;
  }

  /** Repetir o incerto com a chave dele: a rota devolve o já criado e não transfere duas vezes. */
  get isSafeRepeatOfUncertainTransfer(): boolean {
    return this.isRepeatingUncertainTransfer && !!this.uncertainTransfer.idempotencyKey;
  }

  get needsNewTransferConfirmation(): boolean {
    return this.transferOutcomeUnknown && !this.isSafeRepeatOfUncertainTransfer;
  }

  get isTransferInvalid(): boolean {
    return !this.userControlOrigin.value
      || !this.userControlDestination.value
      || this.sameTransferUser
      || this.transferAmountDecimal === null
      // Sem o saldo ainda, `transferExceedsBalance` daria falso e deixaria passar qualquer valor.
      || this.isLoadingOriginBalance
      || this.originCurrencyMismatch
      || this.transferExceedsBalance
      || (this.needsNewTransferConfirmation && !this.confirmNewTransfer.value);
  }

  get bonusEnabled(): boolean {
    return !!this.rechargeForm?.get('bonus_enabled').value;
  }

  /** Valor pago informado, já como string decimal — é o que a rota espera. */
  get totalDecimal(): string | null {
    const raw = this.rechargeForm?.get('total').value;

    if (raw === null || raw === undefined || String(raw).trim() === '') {
      return null;
    }

    const total = Number(raw);

    return isFinite(total) && total > 0 ? String(raw) : null;
  }

  /** A carteira não respondeu: o zero na tela não é resposta sobre a faixa. */
  get isBonusUnknown(): boolean {
    return isBonusUnknown(this.bonusQuote);
  }

  get isInvalid(): boolean {
    if (this.isBalanceTransfer) {
      return this.isTransferInvalid;
    }

    if (this.isRecharge) {
      return this.rechargeForm.invalid;
    }

    if (!this.userControlOrigin.value) {
      return true;
    }

    return !this.formCredits ||
        !this.userControlDestination.value ||
        this.sameTransferUser ||
        this.invalidCredits ||
        (this.needsNewTransferConfirmation && !this.confirmNewTransfer.value);
  }

  /**
   * A origem paga o produto (SMS/ligação) pela carteira (`wallet_sms_billed` /
   * `wallet_call_billed`, que a busca de usuários do admin devolve). Aí o backend não usa o
   * pacote: converte os créditos em R$ pelo preço da origem e confere o saldo da carteira
   * (recusa com `insufficient_funds`). Limitar pelo pacote — zerado na migração — travava a
   * transferência que a carteira faria.
   */
  get originBilledByWallet(): boolean {
    const origin = this.userControlOrigin.value;

    if (!origin) {
      return false;
    }

    return this.isSms ? origin.wallet_sms_billed === true : this.isCall && origin.wallet_call_billed === true;
  }

  get invalidCredits(): boolean {
    if (!this.userControlOrigin.value || this.originBilledByWallet) {
      return false;
    }

    return (this.formCredits > this.availableCredits);
  }

  get availableCredits(): number {
    if (!this.userControlOrigin.value) {
      return null;
    }

    if (this.isSms) {
      return +this.userControlOrigin.value.credits;
    }

    return +this.userControlOrigin.value.call_credits;
  }

  constructor(
    private rechargesService: RechargesService,
    private userService: UserService,
    private walletBonusService: WalletBonusService,
    private walletTransferService: WalletTransferService,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private uncertainTransfers: UncertainTransferStore,
  ) {}

  ngOnInit(): void {
    this.rechargesService
      .price()
      .subscribe(prices => this.prices = prices);

    this.rechargeForm = this.fb.group({
      user_id: [null, Validators.required],
      credits: [null, Validators.required],
      price: [null, Validators.required],
      total: [null, Validators.required],
      payment_type: [null, [Validators.required, Validators.minLength(3)]],
      payment_status: [null, [Validators.required, Validators.minLength(3)]],
      target: [this.target, Validators.required],
      type: [this.type, Validators.required],
      // Ligado por padrão: a recarga manual do admin é uma recarga como outra
      // qualquer, e desligar o bônus é a exceção que se escolhe. Ao escolher a conta,
      // vira a config "Recebe bônus de recarga" dela (CU-17tm466tzkz) — o admin ainda
      // pode marcar/desmarcar à mão para forçar.
      bonus_enabled: [true],
      description: [null]
    });

    this.rechargeForm
      .get('credits')
      .valueChanges.subscribe(credits => {
        this.calculateTotal(credits);
        // Na transferência de créditos é o pedido que mudou.
        this.clearTransferOutcome();
      });

    this.rechargeForm
      .get('total')
      .valueChanges.subscribe(total => {
        this.calculateTargetProductPrice(total);
        this.scheduleBonusQuote();
      });

    this.rechargeForm
      .get('bonus_enabled')
      .valueChanges.subscribe(() => this.scheduleBonusQuote());

    this.rechargeForm
      .get('target')
      .valueChanges.subscribe(target => this.calculateTotal(this.rechargeForm.get('credits').value));

    this.route.params
      .subscribe(params => {
        if (params.userId) {
          this.userService
            .findById(params.userId)
            .subscribe(user => this.userControlDestination.patchValue(user));
        }
      });

    // Transferência de Saldo: o disponível é o da carteira de quem cede.
    this.userControlOrigin.valueChanges.subscribe(() => this.loadOriginBalance());
    this.transferAmount.valueChanges.subscribe(() => this.clearTransferOutcome());

    this.userControlDestination.valueChanges.subscribe(user => {
      this.rechargeForm.patchValue({ user_id: user?.id || null });
      this.clearTransferOutcome();
      // O bônus depende de QUEM recarrega: a cotação do cliente anterior não
      // vale para o novo, e deixá-la na tela mostraria o bônus de outro.
      this.applyAccountBonusDefault(user);
      this.scheduleBonusQuote();
    });
  }

  /**
   * O pedido mudou: a recusa era do pedido anterior, e a confirmação de "outra transferência"
   * também. O pedido incerto (`uncertainTransfer`) fica: ele não deixa de ter acontecido.
   */
  clearTransferOutcome(): void {
    this.transferError = null;
    this.confirmNewTransfer.setValue(false);
  }

  filterDestination(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    const filterValue = this.inputDestination.nativeElement.value?.trim().toLowerCase();

    const user = this.userControlDestination.value;
    if (user && filterValue.length < this.displayUser(user).length) {
      this.userControlDestination.patchValue(null);
      this.filteredUserDestination$ = null;
      return;
    }

    if (filterValue.length >= 3) {
      this.timer = setTimeout(() => {
        this.filteredUserDestination$ = this.userService.filter(filterValue);
      }, 200);
    }
  }

  filterOrigin(): void {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    const filterValue = this.inputOrigin.nativeElement.value?.trim().toLowerCase();

    const user = this.userControlOrigin.value;
    if (user && filterValue.length < this.displayUser(user).length) {
      this.userControlOrigin.patchValue(null);
      this.filteredUserOrigin$ = null;
      return;
    }

    if (filterValue.length >= 3) {
      this.timer = setTimeout(() => {
        this.filteredUserOrigin$ = this.userService.filter(filterValue);
      }, 200);
    }
  }

  displayUser(user?: User): string {
    if (user) {
      return `${user.name} (${user.email})`;
    }

    return '';
  }

  getServiceName(): string {
    return this.isCall ? 'Ligação' : 'SMS';
  }

  targetChanged(target: string): void {
    this.target = target;
    this.rechargeForm.patchValue({ target });

    this.applyBalanceMode();
    this.scheduleBonusQuote();
    this.loadOriginBalance();
  }

  /** Saldo da carteira da origem, para a transferência de Saldo não prometer o que não há. */
  loadOriginBalance(): void {
    const origin = this.userControlOrigin.value;
    this.originBalanceSubscription?.unsubscribe();
    this.originBalanceSubscription = null;
    this.originBalance = null;
    this.originBalanceUnavailable = false;
    this.isLoadingOriginBalance = false;
    // Outra origem (ou outro modo): a recusa era do pedido anterior.
    this.clearTransferOutcome();

    // Saldo em R$ da origem: na transferência de Saldo e na de créditos que a carteira paga.
    if (!(this.isBalanceTransfer || (!this.isRecharge && this.originBilledByWallet)) || !origin?.id) {
      return;
    }

    this.isLoadingOriginBalance = true;
    this.originBalanceSubscription = this.walletTransferService.userBalance(origin.id).subscribe({
      next: (balance) => {
        this.isLoadingOriginBalance = false;
        this.originBalance = balance;
      },
      // Sem o saldo a tela não bloqueia: a carteira recusa o débito sem saldo de qualquer jeito.
      error: () => {
        this.isLoadingOriginBalance = false;
        this.originBalanceUnavailable = true;
      },
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.quoteTimer);
    this.cancelBonusQuote();
    this.accountBonusSubscription?.unsubscribe();
    this.originBalanceSubscription?.unsubscribe();
    // O lançamento em voo (`saveSubscription`) segue até a resposta; ver `destroyed`.
    this.destroyed = true;
  }

  /**
   * Liga/desliga o modo Saldo.
   *
   * No Saldo "Créditos" fica desabilitado em 0 (o `getRawValue` manda o 0, que o backend
   * lê como recarga por valor) e o preço unitário deixa de existir. SMS e Ligação voltam
   * ao pacote: créditos, valor pago e preço obrigatórios.
   */
  applyBalanceMode(): void {
    const credits = this.rechargeForm.get('credits');
    const price = this.rechargeForm.get('price');
    const total = this.rechargeForm.get('total');

    if (this.isBalance) {
      credits.setValue(0, { emitEvent: false });
      credits.disable({ emitEvent: false });
      credits.clearValidators();
      price.setValue(null, { emitEvent: false });
      price.clearValidators();
      total.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      if (credits.disabled) {
        credits.setValue(null, { emitEvent: false });
        credits.enable({ emitEvent: false });
      }
      credits.setValidators(Validators.required);
      price.setValidators(Validators.required);
      total.setValidators(Validators.required);
    }

    credits.updateValueAndValidity({ emitEvent: false });
    price.updateValueAndValidity({ emitEvent: false });
    total.updateValueAndValidity({ emitEvent: false });
  }

  typeChanged(type: string): void {
    this.type = type;
    this.rechargeForm.patchValue({ type });
    this.applyBalanceMode();
    this.scheduleBonusQuote();
    this.loadOriginBalance();
  }

  calculateTotal(credits: number): void {
    // Saldo não tem pacote: o valor pago é o que o admin digita.
    if (this.isBalance) {
      return;
    }

    let total = 0;
    let price = 0;
    let calculate = true;

    const targetPrices = this.prices[this.target] || [];

    if (!targetPrices.length) {
      return;
    }

    targetPrices.forEach(priceInfo => {
      if (calculate && credits <= priceInfo.quantity) {
        price = priceInfo.price;
        calculate = false;
      }
    });

    if (price === 0) {
      price = targetPrices[targetPrices.length - 1].price;
    }

    total = credits * price;

    if (total > 0) {
      this.rechargeForm.patchValue({ total: total.toFixed(2) });
      return;
    }

    this.rechargeForm.patchValue({ total: null });
  }

  calculateTargetProductPrice(total: number): void {
    // Saldo não tem preço unitário: o que entra na carteira é o valor pago.
    if (this.isBalance) { return; }

    const credits = +this.rechargeForm.get('credits').value;

    if (!credits) { return; }

    const servicePrice = total / credits;

    this.rechargeForm.patchValue({ price: servicePrice.toFixed(3) });
  }

  /**
   * Cota o bônus depois que o valor para de mudar.
   *
   * Sem a espera, cada tecla do campo "Valor Pago" viraria uma requisição — e o
   * campo é recalculado automaticamente a cada mudança de créditos.
   */
  scheduleBonusQuote(): void {
    if (this.quoteTimer) {
      clearTimeout(this.quoteTimer);
    }

    // Valor ou cliente mudou: a cotação em voo e a da tela são do pedido anterior — durante a
    // espera a tela mostraria o bônus do cliente anterior para o novo.
    this.cancelBonusQuote();
    this.bonusQuote = null;
    this.bonusQuoteError = false;

    if (!this.showsBonus || !this.bonusEnabled || !this.totalDecimal) {
      return;
    }

    this.quoteTimer = setTimeout(() => this.quoteBonus(), BONUS_QUOTE_DEBOUNCE_MS);
  }

  /**
   * O switch "dar bônus" nasce com a config da conta (CU-17tm466tzkz).
   *
   * O objeto da busca de usuários pode não trazer a coluna: aí a conta é lida inteira.
   * Uma resposta atrasada de outra conta não vale (o admin trocou de destino no meio).
   * Leitura que falha deixa `null` — e o `save()` omite o campo se o admin não mexeu
   * no switch, para o backend decidir pela config da conta em vez de forçar no escuro.
   */
  private applyAccountBonusDefault(user: User | null): void {
    this.accountBonusSubscription?.unsubscribe();
    this.accountBonusSubscription = null;
    this.accountReceivesBonus = null;
    // Outra conta, outro switch: volta ao neutro (marcado e intocado). O valor e o "mexido"
    // da conta anterior não valem para esta — senão uma leitura que falha deixaria a tela
    // prometendo "sem bônus" enquanto o `save()` omite o campo e o backend dá o bônus.
    const control = this.rechargeForm.get('bonus_enabled');
    control.setValue(true, { emitEvent: false });
    control.markAsPristine();

    if (!user?.id) {
      return;
    }

    // `0`/`1` também é a config (review 4): só "não veio" faz ler a conta inteira.
    const known = parseRechargeBonusFlag(user.recharge_bonus_enabled);

    if (known !== null) {
      this.setAccountReceivesBonus(known);
      return;
    }

    // Trocar de conta cancela esta leitura (o `unsubscribe` no topo): a resposta que
    // chega aqui é sempre da conta da tela.
    this.accountBonusSubscription = this.userService.findById(user.id).subscribe({
      next: (full) => {
        this.learnAccountReceivesBonus(receivesRechargeBonus(full?.recharge_bonus_enabled));
        this.scheduleBonusQuote();
      },
      // Falhou: `accountReceivesBonus` segue `null` (zerado acima). A cotação ainda pode
      // dizer a config da conta (`bonus_eligible`, ver `quoteBonus`).
      error: () => undefined,
    });
  }

  /**
   * A config da conta chegou (pela leitura da conta ou pela cotação). Switch intocado:
   * ele passa a mostrar a config. O admin já mexeu: a escolha dele vale, e a config só
   * fica registrada (para o aviso e a prévia forçada).
   */
  private learnAccountReceivesBonus(receives: boolean): void {
    if (this.rechargeForm.get('bonus_enabled').dirty) {
      this.accountReceivesBonus = receives;
    } else {
      this.setAccountReceivesBonus(receives);
    }
  }

  private setAccountReceivesBonus(receives: boolean): void {
    this.accountReceivesBonus = receives;
    const control = this.rechargeForm.get('bonus_enabled');
    control.setValue(receives, { emitEvent: false });
    control.markAsPristine();
  }

  /** O admin marcou "dar bônus" numa conta que não recebe: é forçar. */
  get isBonusForced(): boolean {
    return this.bonusEnabled && this.accountReceivesBonus === false;
  }

  quoteBonus(): void {
    const total = this.totalDecimal;

    if (!total) {
      return;
    }

    this.isQuotingBonus = true;

    this.bonusQuoteSubscription = this.walletBonusService
      .quote(total, this.userControlDestination.value?.id || null, this.isBonusForced)
      .subscribe({
        next: (quote) => {
          this.isQuotingBonus = false;

          // A leitura da conta falhou, mas a cotação também diz se a conta recebe bônus
          // (`bonus_eligible`, review 3). Sem isto, o admin que mexe no switch de uma conta
          // opt-out via a prévia SEM forçar (R$ 0) e o `save()` mandava `bonus_enabled`
          // — o backend forçava e pagava a faixa. Se a config aprendida muda a prévia
          // (vira forçada, ou o switch intocado passa a "sem bônus"), cota de novo.
          if (this.accountReceivesBonus === null && typeof quote?.bonus_eligible === 'boolean') {
            const forcedBefore = this.isBonusForced;
            this.learnAccountReceivesBonus(quote.bonus_eligible);

            if (this.isBonusForced !== forcedBefore || !this.bonusEnabled) {
              this.scheduleBonusQuote();
              return;
            }
          }

          this.bonusQuote = quote;
        },
        error: () => {
          this.isQuotingBonus = false;
          // Falhar a cotação NÃO pode impedir a recarga: quem decide o bônus é o
          // backend no momento do lançamento, e esta caixa é informativa.
          this.bonusQuote = null;
          this.bonusQuoteError = true;
        },
      });
  }

  private cancelBonusQuote(): void {
    this.bonusQuoteSubscription?.unsubscribe();
    this.bonusQuoteSubscription = null;
    this.isQuotingBonus = false;
  }

  /**
   * A transferência é registrada na hora, mas o dinheiro anda pela fila: sai da origem quando a
   * carteira confirma o débito, e só então entra no destino (cerca de um minuto cada passo).
   */
  saveBalanceTransfer(): void {
    if (this.isTransferInvalid) {
      return;
    }

    const originUserId = this.userControlOrigin.value.id;
    const destinationUserId = this.userControlDestination.value.id;
    const amount = this.transferAmountDecimal;
    const fingerprint = this.transferFingerprint;
    const idempotencyKey = this.transferIdempotencyKeyFor(fingerprint);
    // O dono do pedido é o do ENVIO: a resposta pode chegar com outro cliente simulado.
    const owner = this.uncertainTransfers.currentOwner();
    // O que foi pedido, capturado no envio: os campos seguem editáveis com a requisição em voo.
    const label = money.transform(this.transferAmountMicros, TRANSFER_CURRENCY, undefined, 2);
    const destinationLabel = this.displayUser(this.userControlDestination.value);

    this.isSaving = true;
    this.transferError = null;

    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.walletTransferService.create({
      origin_user_id: originUserId,
      destination_user_id: destinationUserId,
      amount,
      description: this.rechargeForm.get('description').value || null,
      idempotency_key: idempotencyKey,
    }).subscribe({
      next: (res) => {
        this.isSaving = false;
        // Tentativa concluída: a próxima transferência é outra, com outra chave.
        this.resetTransferIdempotencyKey();
        // Desfecho definitivo (criada, ou o replay de uma que falhou): o pedido deixa de ser incerto.
        this.settleUncertainTransfer(fingerprint, owner);

        const failed = failedReplayMessage(res?.data);

        if (failed) {
          // Um texto só no campo e no toast.
          this.transferError = failed;
          this.toast.error(failed);
          return;
        }

        this.toast.success('Transferência registrada. O valor sai da origem e entra no destino quando a carteira confirmar o débito.');
        this.leaveAfterSave();
      },
      error: (res) => {
        this.isSaving = false;

        // Sem resposta (0) ou erro do servidor (5xx): pode ter sido registrada. A chave fica
        // a mesma, então repetir é seguro; outro pedido passa pela confirmação.
        if (isUncertainOutcome(res)) {
          this.markUncertainTransfer(fingerprint, owner, idempotencyKey, label, destinationLabel);
          this.toast.error(TRANSFER_OUTCOME_UNKNOWN_MESSAGE);
          return;
        }

        // Um texto só no campo e no toast.
        this.transferError = refusalMessage(res, TRANSFER_REFUSED_MESSAGE);
        this.handleRefusal(res, fingerprint, owner, idempotencyKey);

        // O 401 já vira toast no interceptador; o 422 não (`OWN_ERROR_TOAST`), então avisa aqui
        // com o mesmo texto do campo, como o 403, 404, 409…
        if (res?.status !== 401) {
          this.toast.error(this.transferError);
        }
      },
    });
  }

  /**
   * Transferência de créditos de SMS/ligação (`POST /recharges/add`, `type: 'transfer'`). O
   * backend escolhe o caminho por quem paga o produto em cada conta (front-contrato-r2): no
   * pacote responde 200 vazio e move as unidades; na carteira converte as unidades em R$ pelo
   * preço da origem e responde 201 com a transferência, que debita a origem antes de creditar o
   * destino. A `idempotency_key` deduplica o reenvio nos dois caminhos (carteira:
   * front-contrato-r3 §4; pacote: front-contrato-r4 §2), então depois de um desfecho incerto
   * repetir o mesmo pedido vai com a mesma chave, sem confirmação, como no Saldo. Com a carteira
   * ainda não autoritativa o produto cobrado por ela é recusado (422 `wallet_not_authoritative`,
   * front-contrato-r4 §1).
   */
  saveCreditsTransfer(formData: any): void {
    if (this.isInvalid) {
      return;
    }

    const fingerprint = this.transferFingerprint;
    const idempotencyKey = this.transferIdempotencyKeyFor(fingerprint);
    // O dono do pedido é o do ENVIO: a resposta pode chegar com outro cliente simulado.
    const owner = this.uncertainTransfers.currentOwner();
    const label = `${this.formCredits} créditos de ${this.getServiceName()}`;
    const destinationLabel = this.displayUser(this.userControlDestination.value);

    this.isSaving = true;
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.rechargesService.transferCredits({ ...formData, idempotency_key: idempotencyKey }).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.resetTransferIdempotencyKey();
        // Desfecho definitivo (criada, ou o replay de uma que falhou): o pedido deixa de ser incerto.
        this.settleUncertainTransfer(fingerprint, owner);

        const failed = failedReplayMessage(res?.data);

        if (failed) {
          this.toast.error(failed);
          return;
        }

        this.toast.success(res?.data
          ? `Transferência registrada: ${money.transform(res.data.amount_micros, res.data.currency || TRANSFER_CURRENCY, undefined, 2)} `
            + 'sai da conta de origem; o destino recebe quando a carteira confirmar o débito.'
          : 'Créditos transferidos com sucesso!');
        this.leaveAfterSave();
      },
      error: (res) => {
        this.isSaving = false;

        if (isUncertainOutcome(res)) {
          this.markUncertainTransfer(fingerprint, owner, idempotencyKey, label, destinationLabel);
          this.toast.error(TRANSFER_OUTCOME_UNKNOWN_MESSAGE);
          return;
        }

        this.handleRefusal(res, fingerprint, owner, idempotencyKey);

        if (res?.status !== 401) {
          this.toast.error(refusalMessage(res, CREDITS_TRANSFER_REFUSED_MESSAGE));
        }
      },
    });
  }

  private markUncertainTransfer(
    fingerprint: string,
    owner: string,
    idempotencyKey: string,
    label: string,
    destinationLabel: string
  ): void {
    this.uncertainTransfers.save({ fingerprint, idempotencyKey, label, destinationLabel }, owner);
    this.lastUncertainFingerprint = fingerprint;
    this.confirmNewTransfer.setValue(false);
  }

  private settleUncertainTransfer(fingerprint: string, owner: string): void {
    this.uncertainTransfers.remove(fingerprint, owner);
    if (this.lastUncertainFingerprint === fingerprint) {
      this.lastUncertainFingerprint = null;
    }
  }

  /**
   * Recusa certa. `idempotency_key_reused`: a chave já pertence a outro payload — guardá-la faria
   * toda nova tentativa voltar 422; se era a do pedido incerto, repeti-lo deixa de ser seguro e
   * passa pela confirmação. Outro 422 ao repetir o pedido incerto COM A CHAVE DELE é desfecho
   * definitivo: se a primeira tentativa tivesse criado algo, o backend devolveria a já criada.
   */
  private handleRefusal(res: any, fingerprint: string, owner: string, idempotencyKey: string): void {
    const uncertain = this.uncertainTransfers.find(fingerprint, owner);

    if (res?.error?.code === 'idempotency_key_reused') {
      this.resetTransferIdempotencyKey();
      if (uncertain?.idempotencyKey === idempotencyKey) {
        this.uncertainTransfers.save({ ...uncertain, idempotencyKey: null }, owner);
      }
      return;
    }

    if (res?.status === 422 && uncertain?.idempotencyKey === idempotencyKey) {
      this.settleUncertainTransfer(fingerprint, owner);
    }
  }

  /**
   * A mesma chave para o mesmo pedido (produto, origem, destino e quantidade) até dar certo; outra chave
   * quando o pedido muda. É o que torna seguro repetir depois de um desfecho incerto — e o
   * pedido incerto guarda a dele, então voltar a ele depois de mexer no valor também é seguro.
   */
  private transferIdempotencyKeyFor(fingerprint: string): string {
    if (this.isRepeatingUncertainTransfer && this.uncertainTransfer.idempotencyKey) {
      this.transferIdempotencyKey = this.uncertainTransfer.idempotencyKey;
      this.transferIdempotencyFingerprint = fingerprint;
    }

    if (!this.transferIdempotencyKey || this.transferIdempotencyFingerprint !== fingerprint) {
      this.transferIdempotencyKey = newIdempotencyKey();
      this.transferIdempotencyFingerprint = fingerprint;
    }

    return this.transferIdempotencyKey;
  }

  private resetTransferIdempotencyKey(): void {
    this.transferIdempotencyKey = null;
    this.transferIdempotencyFingerprint = null;
  }

  /** Depois do lançamento volta para a lista — se o admin ainda está nesta tela. */
  private leaveAfterSave(): void {
    if (!this.destroyed) {
      this.router.navigate(['/recharges']);
    }
  }

  back(): void {
    window.history.back();
  }

  save(): void {
    if (this.isBalanceTransfer) {
      this.saveBalanceTransfer();
      return;
    }

    const formData: any = this.rechargeForm.getRawValue();

    if (!this.isRecharge) {
      formData.user_id_origin = this.userControlOrigin.value.id;
      // Transferência move saldo entre contas: não há recarga, então não há
      // faixa de bônus para aplicar nem para o backend ter de ignorar.
      delete formData.bonus_enabled;
      this.saveCreditsTransfer(formData);
      return;
    }

    let successMessage = 'Créditos cadastrados com sucesso! Basta confirmá-los!';
    const errorMessage = 'Erro ao realizar recarga! Tente novamente mais tarde.';

    if (this.isBalance) {
      // O mesmo corpo do "Adicionar saldo" do cliente: alvo `sms`, `credits` 0, valor pago.
      formData.target = BALANCE_PAYLOAD_TARGET;
      formData.credits = 0;
      formData.price = 0;
      successMessage = 'Saldo cadastrado com sucesso! Basta confirmá-lo para entrar na carteira.';

      // Switch intocado: sem o campo, o backend aplica "Recebe bônus de recarga" da conta
      // como ela está AGORA (CU-17tm466tzkz). Com a config desconhecida (a leitura
      // falhou), mandar o `true` do formulário seria forçar o bônus sem o admin ter
      // decidido; com ela conhecida, mandar o valor da tela gravaria uma config que pode
      // ter mudado desde que a tela a leu (review 4). Só a escolha do admin vai no corpo.
      if (this.rechargeForm.get('bonus_enabled').pristine) {
        delete formData.bonus_enabled;
      }
    } else {
      // Bônus é só do Saldo (ver `showsBonus`).
      delete formData.bonus_enabled;
    }

    this.isSaving = true;
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = this.rechargesService
      .add(formData)
      .subscribe({
        next: (res) => {
          this.isSaving = false;
          this.toast.success(successMessage);
          this.leaveAfterSave();
        },
        error: (res) => {
          this.isSaving = false;
          // A requisição leva `OWN_ERROR_TOAST`: o interceptador só avisa o 401, e o resto é
          // avisado aqui, uma vez — sem adivinhar o que ele já mostrou.
          if (res?.status === 401) {
            return;
          }

          this.toast.error(res?.status === 0 ? RECHARGE_OUTCOME_UNKNOWN_MESSAGE : refusalMessage(res, errorMessage));
        }
      });
  }

}
